import { Link } from "@tanstack/react-router";
import { ArrowRight, ExternalLink, Phone, CheckCircle2, Star, ShieldCheck, Wrench, Award } from "lucide-react";
import { CHECK_BRANDS, SERVICES, type Service, type CheckBrand } from "@/lib/services";
import { ACCREDITATION_LOGOS, AWARDS, COVERAGE_AREAS, SITE, TESTIMONIALS } from "@/lib/site";
import { type HeroFloatImage } from "@/components/hero-service-float-overlay";
import { ServiceHeroFloatLayer } from "@/components/service-hero-float-layer";
import { lazy, Suspense, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const ServiceEnquiryDialog = lazy(() =>
  import("@/components/service-enquiry-dialog").then((m) => ({ default: m.ServiceEnquiryDialog })),
);

const GoogleReviewsCarousel = lazy(() =>
  import("@/components/google-reviews-carousel").then((m) => ({
    default: m.GoogleReviewsCarousel,
  })),
);

const TrustpilotReviewsCarousel = lazy(() =>
  import("@/components/trustpilot-reviews-carousel").then((m) => ({
    default: m.TrustpilotReviewsCarousel,
  })),
);

export function HeroSplit({
  eyebrow,
  brandLogo,
  brandLogoAlt,
  title,
  headline,
  description,
  image,
  imageAlt,
  imageSources,
  imageClassName,
  /** Use the brand textured navy field at every breakpoint instead of the photo. */
  hideImageOnMobile = false,

  /** Soft floating service-card images behind hero copy. */
  serviceFloatOverlay = false,
  /** Optional page-scoped image list for the float overlay. Passing this implies serviceFloatOverlay=true. */
  floatOverlayImages,
  ctaPrimary,
  ctaSecondary,
  formSlot,
  heroChips,
}: {
  eyebrow?: string;
  brandLogo?: string;
  brandLogoAlt?: string;
  title: React.ReactNode;
  headline?: React.ReactNode;
  description: React.ReactNode;
  image: string;
  imageAlt: string;
  imageSources?: {
    avif?: { src: string; media: string }[];
    webp?: { src: string; media: string }[];
  };
  /** Optional extras for the hero <img> (e.g. object-position for subject framing). */
  imageClassName?: string;
  hideImageOnMobile?: boolean;
  serviceFloatOverlay?: boolean;
  floatOverlayImages?: HeroFloatImage[];
  ctaPrimary?: { label: string; to?: string; href?: string };
  ctaSecondary?: { label: string; to?: string; href?: string };
  formSlot?: React.ReactNode;
  heroChips?: { icon: "wrench" | "shield" | "award"; label: string }[];
}) {
  return (
    <section className="relative overflow-hidden bg-primary">
      {/* Base z-0: textured navy field (used at all breakpoints when hideImageOnMobile is set) */}
      {hideImageOnMobile && (
        <div className="absolute inset-0 z-0" aria-hidden>
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-[color-mix(in_oklab,var(--primary)_78%,black)]" />
          <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-accent/25 blur-3xl" />
          <div className="absolute top-1/3 right-1/4 h-56 w-56 rounded-full bg-accent/20 blur-3xl" />
          <div className="absolute bottom-[-4rem] left-[-3rem] h-64 w-64 rounded-full bg-accent-soft/15 blur-3xl" />
          <div className="absolute top-1/2 left-1/3 h-52 w-52 -translate-y-1/2 rounded-full bg-accent/15 blur-3xl" />
          <div className="absolute bottom-8 right-1/3 h-40 w-40 rounded-full bg-accent/20 blur-2xl" />

          <div
            className="absolute inset-0 opacity-[0.14]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.55) 1px, transparent 0)",
              backgroundSize: "18px 18px",
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
        </div>
      )}

      {!hideImageOnMobile && (
        <div className="absolute inset-0 z-0" aria-hidden={undefined}>
          <picture>
            {imageSources?.avif?.map((s) => (
              <source key={`avif-${s.media}`} srcSet={s.src} type="image/avif" media={s.media} />
            ))}
            {imageSources?.webp?.map((s) => (
              <source key={`webp-${s.media}`} srcSet={s.src} type="image/webp" media={s.media} />
            ))}
            <img
              src={image}
              alt=""
              className={cn("w-full h-full object-cover", imageClassName)}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width={1920}
              height={1080}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/20 to-primary/30 md:from-primary/20 md:to-transparent" />
        </div>
      )}


      {(serviceFloatOverlay || (floatOverlayImages && floatOverlayImages.length > 0)) && (
        <ServiceHeroFloatLayer images={floatOverlayImages} />
      )}

      <div className="container-page relative z-[3] py-16 md:py-24 lg:py-28">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3 text-primary-foreground">
            {brandLogo && (
              <div className="mb-6">
                <img
                  src={brandLogo}
                  alt={brandLogoAlt ?? "Brand logo"}
                  className="h-20 md:h-28 lg:h-32 w-auto"
                  loading="eager"
                  decoding="async"
                  width={600}
                  height={240}
                />
              </div>
            )}
            {!brandLogo && eyebrow && (
              <p className="inline-block text-xs font-semibold tracking-wider uppercase bg-accent/10 text-accent border border-accent/30 px-3 py-1 rounded-full mb-4">
                {eyebrow}
              </p>
            )}
            <h1
              className={
                headline
                  ? "sr-only"
                  : "text-4xl md:text-5xl lg:text-6xl font-bold text-white text-balance leading-[1.05]"
              }
            >
              {title}
            </h1>
            {headline && (
              <p className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-balance leading-[1.05]">
                {headline}
              </p>
            )}
            <p
              className={cn(
                "text-lg md:text-xl text-primary-foreground/90 max-w-2xl text-pretty",
                headline ? "mt-3" : "mt-5",
              )}
            >
              {description}
            </p>
            {heroChips && heroChips.length > 0 && (
              <div className={cn("flex flex-wrap gap-2", headline ? "mt-4" : "mt-6")}>
                {heroChips.map((c) => {
                  const Icon = c.icon === "wrench" ? Wrench : c.icon === "shield" ? ShieldCheck : Award;
                  return (
                    <span
                      key={c.label}
                      className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm px-4 py-1.5 text-sm font-semibold text-primary-foreground"
                    >
                      <Icon className="h-4 w-4 text-accent" aria-hidden />
                      {c.label}
                    </span>
                  );
                })}
              </div>
            )}
            {(ctaPrimary || ctaSecondary) && (
              <div className={cn("flex flex-wrap gap-3", headline ? "mt-5" : "mt-7")}>
                {ctaPrimary &&
                  (ctaPrimary.to ? (
                    <Link
                      to={ctaPrimary.to}
                      className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
                    >
                      {ctaPrimary.label}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                  ) : (
                    <a
                      href={ctaPrimary.href}
                      className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
                    >
                      {ctaPrimary.label}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </a>
                  ))}
                {ctaSecondary &&
                  (ctaSecondary.to ? (
                    <Link
                      to={ctaSecondary.to}
                      className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 backdrop-blur-sm px-6 py-3.5 font-semibold text-primary-foreground hover:bg-white/20 transition-colors"
                    >
                      {ctaSecondary.label}
                    </Link>
                  ) : (
                    <a
                      href={ctaSecondary.href}
                      className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 backdrop-blur-sm px-6 py-3.5 font-semibold text-primary-foreground hover:bg-white/20 transition-colors"
                    >
                      {ctaSecondary.label}
                    </a>
                  ))}
              </div>
            )}
          </div>
          {formSlot && <div className="lg:col-span-2">{formSlot}</div>}
        </div>
      </div>
      <span className="sr-only">{imageAlt}</span>
    </section>
  );
}

export function PageHero({
  eyebrow,
  title,
  description,
  image,
  imageAlt,
  objectPosition = "center",
}: {
  eyebrow?: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  objectPosition?: string;
}) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover"
          style={{ objectPosition }}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60" />
      </div>
      <div className="container-page relative py-20 md:py-28 text-primary-foreground">
        {eyebrow && (
          <p className="text-sm font-semibold tracking-wider uppercase text-accent-soft mb-3">
            {eyebrow}
          </p>
        )}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold max-w-4xl text-balance">{title}</h1>
        <p className="mt-5 text-lg md:text-xl text-primary-foreground/85 max-w-3xl">{description}</p>
      </div>
    </section>
  );
}

export function TrustStrip({ claims }: { claims: string[] }) {
  return (
    <section className="bg-card border-y">
      <div className="container-page py-6">
        <div className="grid sm:grid-cols-3 gap-4">
          {claims.map((claim) => (
            <div key={claim} className="flex items-center gap-3">
              <div className="rounded-full bg-success/10 p-2">
                <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
              </div>
              <p className="text-sm md:text-base font-semibold">{claim}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-6 border-t border-border">
          <AccreditationStrip />
        </div>
      </div>
    </section>
  );
}

export function Section({
  children,
  className,
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cn("py-16 md:py-24", className)}>
      <div className="container-page">{children}</div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  align = "left",
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "left" | "center";
}) {
  return (
    <div className={cn("max-w-3xl mb-10", align === "center" && "mx-auto text-center")}>
      {eyebrow && (
        <p className="text-xs font-semibold tracking-wider uppercase text-accent mb-3">{eyebrow}</p>
      )}
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-balance">{title}</h2>
      {description && (
        <p className="mt-4 text-base md:text-lg text-muted-foreground text-pretty">{description}</p>
      )}
    </div>
  );
}

const SERVICE_LP_PATHS: Record<string, string> = {
  "cavity-wall-insulation-removal": "/lp/cavity-insulation-removal",
  "cavity-wall-insulation": "/lp/cavity-wall-insulation",
  "damp-and-mould-remediation": "/damp-survey",
  "loft-insulation": "/loft-insulation",
  "internal-wall-insulation": "/internal-wall-insulation",
  "remedial-wall-ties": "/remedial-wall-ties",
  "ventilation-and-extraction": "/ventilation-extraction",
  "boilers-and-heating": "/boilers-heating",
  "heating-controls": "/heating-controls",
  "air-source-heat-pumps": "/air-source-heat-pumps",
  "solar-pv": "/solar-pv",
};

export function ServiceCard({ service }: { service: Service }) {
  const Icon = service.icon;
  const [open, setOpen] = useState(false);
  const lpPath = SERVICE_LP_PATHS[service.slug];
  return (
    <>
      <div className="group flex flex-col rounded-2xl border bg-card overflow-hidden hover:shadow-lift hover:-translate-y-1 transition-all">
        <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
          <img
            src={service.hero800 ?? service.hero}
            srcSet={
              service.hero800
                ? `${service.hero800} 800w, ${service.hero1280 ?? service.hero} 1280w`
                : undefined
            }
            sizes="(max-width: 767px) 100vw, (max-width: 1279px) 50vw, 33vw"
            alt={`${service.shortName} service by CRS Energy`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
            width={1280}
            height={720}
          />
          <div className="absolute top-3 left-3 rounded-lg bg-background/90 backdrop-blur p-2 text-primary shadow-soft">
            <Icon className="h-5 w-5" aria-hidden />
          </div>
        </div>
        <div className="flex flex-col p-5 grow">
          <h3 className="text-lg font-semibold">{service.shortName}</h3>
          <p className="mt-2 text-sm text-muted-foreground line-clamp-3 grow">{service.tagline}</p>
          <div className="mt-4 flex items-center justify-between gap-3">
            {lpPath ? (
              <Link
                to={lpPath}
                className="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline"
              >
                View More <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:bg-accent/90 transition-colors shadow-cta"
            >
              Enquire
            </button>
          </div>
        </div>
      </div>
      {open && (
        <Suspense fallback={null}>
          <ServiceEnquiryDialog service={service} open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </>
  );
}


export function ServicesGrid({ services = SERVICES }: { services?: Service[] }) {
  const ordered = [...services].sort((a, b) => {
    const aHas = SERVICE_LP_PATHS[a.slug] ? 0 : 1;
    const bHas = SERVICE_LP_PATHS[b.slug] ? 0 : 1;
    return aHas - bHas;
  });
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {ordered.map((s) => (
        <ServiceCard key={s.slug} service={s} />
      ))}
    </div>
  );
}

export function HomeGroupGrid() {
  const order: CheckBrand[] = ["HDC", "HIC", "HRC", "HGC"];
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {order.map((key) => {
          const brand = CHECK_BRANDS[key];
          return (
            <a
              key={key}
              href={brand.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col rounded-2xl border bg-card p-6 hover:shadow-lift hover:-translate-y-1 transition-all"
            >
              <div className="h-16 flex items-center justify-between">
                <img
                  src={brand.logo}
                  alt={`${brand.name} logo`}
                  className="h-14 w-auto object-contain"
                  loading="lazy"
                />
                <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-accent shrink-0" aria-hidden />
              </div>
              <h3 className="mt-4 text-lg font-semibold">{brand.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground grow">{brand.tagline}</p>
              <span className="mt-4 text-sm font-semibold text-accent">Visit site →</span>
            </a>
          );
        })}
      </div>
      <div className="relative mt-8">
        <div className="hidden lg:block absolute left-1/2 -top-8 w-px h-8 bg-gradient-to-b from-transparent to-accent/40" aria-hidden />
        <div className="rounded-2xl bg-primary text-primary-foreground p-6 md:p-8 text-center shadow-lift">
          <p className="text-xs font-semibold tracking-wider uppercase text-accent-soft mb-2">
            CRS delivers the work
          </p>
          <h3 className="text-2xl md:text-3xl font-bold">
            They Diagnose. We Deliver. Across One Trusted Home Services Group.
          </h3>
        </div>
      </div>
    </div>
  );
}

export function SafeHandsBand() {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/85 text-primary-foreground p-8 md:p-10 shadow-lift">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-accent-soft mb-3">
            CRS: you're in safe hands
          </p>
          <h3 className="text-2xl md:text-3xl font-bold leading-tight">
            Over 50 Years Of Combined Experience Delivering Insulation, Damp & Heating Done Right.
          </h3>
        </div>
        <div className="flex md:flex-col items-center md:items-end gap-4 md:gap-1 shrink-0">
          <div className="text-5xl md:text-6xl font-bold text-accent-soft leading-none">50+</div>
          <div className="text-sm uppercase tracking-wider text-primary-foreground/80">Years experience</div>
        </div>
      </div>
    </div>
  );
}

export function PillarTrio() {
  const pillars = [
    {
      icon: Wrench,
      title: "The Right Tools",
      body: "We use specialist insulation extraction machinery, ensuring a first time fix for all properties.",
    },
    {
      icon: ShieldCheck,
      title: "The Right Price",
      body: "We quote for a complete service with a competitive pricing structure to fit around your budget.",
    },
    {
      icon: Award,
      title: "The Right Experience",
      body: "CRS are a fully certified company and our staff hold accreditations recognised throughout the industry.",
    },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {pillars.map((p) => {
        const Icon = p.icon;
        return (
          <div key={p.title} className="group rounded-2xl bg-card border p-7 hover:shadow-lift hover:-translate-y-1 hover:border-accent/40 transition-all">
            <div className="rounded-xl bg-accent/10 p-3 w-fit text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
              <Icon className="h-7 w-7" aria-hidden />
            </div>
            <h3 className="mt-5 text-xl font-semibold">{p.title}</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">{p.body}</p>
          </div>
        );
      })}
    </div>
  );
}

export function HowWeSupportYou() {
  const items = [
    { title: "Government-Backed Funding:", body: "Supporting applicants in the Boiler Upgrade Scheme." },
    { title: "Finance Options:", body: "Monthly payment plans may be available to spread the cost of your upgrade." },
    { title: "Accredited & Compliant:", body: "TrustMark registered, PAS 2030/2035." },
    { title: "Peace Of Mind:", body: "All installations backed by independent guarantees/warranties." },
    { title: "End To End Service:", body: "Everything managed in house, no middlemen, no confusion." },
    { title: "Local Authority Partnerships:", body: "Working with councils and regional authorities to deliver funded energy upgrades." },
    { title: "Energy Efficiency Improvements:", body: "Installing high-quality measures designed to reduce energy costs." },
    { title: "Home Comfort Upgrades:", body: "Improving warmth and efficiency for a more comfortable home." },
  ];
  return (
    <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
      {items.map((item) => (
        <div key={item.title} className="flex gap-4 items-start">
          <CheckCircle2 className="h-10 w-10 shrink-0 text-accent" strokeWidth={2.5} aria-hidden />
          <div>
            <h3 className="text-lg md:text-xl font-bold text-primary leading-tight">{item.title}</h3>
            <p className="mt-1 text-accent font-semibold leading-snug">{item.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function BenefitsList({ benefits }: { benefits: string[] }) {
  return (
    <ul className="space-y-3">
      {benefits.map((b) => (
        <li key={b} className="flex gap-3 items-start">
          <span className="mt-1 rounded-full bg-success/10 p-1 shrink-0">
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
          </span>
          <span className="text-pretty">{b}</span>
        </li>
      ))}
    </ul>
  );
}

const WHY_CHOOSE_US: { icon: typeof Wrench; title: string; body: string }[] = [
  {
    icon: Award,
    title: "50+ Years Combined Experience",
    body: "A specialist team with deep knowledge of UK housing stock, retrofit standards and grant-funded works.",
  },
  {
    icon: ShieldCheck,
    title: "PAS 2030 & TrustMark Certified",
    body: "Independently audited installer competence and on-site compliance with the standards lenders, surveyors and grant bodies expect.",
  },
  {
    icon: CheckCircle2,
    title: "25-Year Independent Guarantees",
    body: "Headline measures backed by independent guarantees, transferable on sale and recognised by mortgage lenders.",
  },
  {
    icon: Star,
    title: "5-Star Customer Service Record",
    body: "On time, clean, communicative installs across Wales and the South West, trusted by homeowners, insurers and housing associations.",
  },
];

export function WhyChooseCRS() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {WHY_CHOOSE_US.map((w) => {
        const Icon = w.icon;
        return (
          <div
            key={w.title}
            className="rounded-2xl border bg-card p-6 hover:shadow-lift hover:-translate-y-1 transition-all"
          >
            <div className="rounded-xl bg-accent/10 text-accent p-3 w-fit">
              <Icon className="h-6 w-6" aria-hidden />
            </div>
            <h3 className="mt-4 text-base font-semibold leading-snug">{w.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.body}</p>
          </div>
        );
      })}
    </div>
  );
}

export function UKMap({ compact = false }: { compact?: boolean }) {
  // Approximate UK silhouette path covering Wales, England, Scotland (stylised)
  const ukPath =
    "M210 30 L225 28 L235 38 L240 55 L232 70 L240 85 L255 90 L262 105 L255 122 L268 132 L260 150 L275 160 L268 178 L280 188 L268 205 L255 215 L268 232 L255 248 L268 262 L255 275 L240 280 L228 295 L235 312 L228 330 L240 345 L232 362 L218 372 L205 368 L195 380 L188 372 L175 380 L168 372 L155 380 L148 370 L138 378 L128 370 L120 380 L112 370 L100 378 L90 365 L82 372 L72 362 L62 372 L52 360 L48 342 L58 330 L52 315 L62 300 L55 285 L65 272 L58 258 L68 245 L60 230 L72 218 L65 205 L78 192 L72 178 L85 168 L78 152 L92 142 L85 128 L98 118 L92 102 L108 92 L102 78 L118 70 L112 58 L130 52 L138 40 L155 38 L168 30 L185 32 L198 28 Z";
  
  const cities = [
    { x: 142, y: 312, label: "Bridgend HQ", hq: true },
    { x: 168, y: 308, label: "Cardiff" },
    { x: 118, y: 308, label: "Swansea" },
    { x: 195, y: 318, label: "Bristol" },
    { x: 205, y: 328, label: "Bath" },
    { x: 168, y: 340, label: "Taunton" },
    { x: 148, y: 358, label: "Exeter" },
    { x: 118, y: 372, label: "Plymouth" },
    { x: 175, y: 295, label: "Newport" },
    { x: 105, y: 295, label: "Carmarthen" },
    { x: 145, y: 270, label: "Brecon" },
    { x: 220, y: 305, label: "Swindon" },
  ];

  return (
    <div className="rounded-2xl border bg-gradient-to-br from-primary-soft/40 via-card to-card p-6 shadow-soft overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-accent">Coverage map</p>
          <h3 className="text-lg font-semibold mt-0.5">Wales & South West England</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-accent" /> HQ
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-primary" /> Service area
          </span>
        </div>
      </div>
      <div className="relative aspect-[4/5] md:aspect-[3/4]">
        <svg viewBox="0 0 320 420" className="w-full h-full" aria-label="Map of CRS service area in the UK">
          <defs>
            <radialGradient id="seaGrad" cx="0.5" cy="0.5" r="0.7">
              <stop offset="0%" stopColor="oklch(0.96 0.02 240)" />
              <stop offset="100%" stopColor="oklch(0.92 0.03 240)" />
            </radialGradient>
            <linearGradient id="landGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="oklch(0.94 0.04 150)" />
              <stop offset="100%" stopColor="oklch(0.88 0.06 160)" />
            </linearGradient>
            <radialGradient id="pulseGrad" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="oklch(0.69 0.17 47)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="oklch(0.69 0.17 47)" stopOpacity="0" />
            </radialGradient>
            <pattern id="dots" width="8" height="8" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.5" fill="oklch(0.7 0.02 240)" opacity="0.3" />
            </pattern>
          </defs>
          
          {/* Sea background */}
          <rect width="320" height="420" fill="url(#seaGrad)" />
          <rect width="320" height="420" fill="url(#dots)" />
          
          {/* UK landmass */}
          <path
            d={ukPath}
            fill="url(#landGrad)"
            stroke="oklch(0.7 0.05 160)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          
          {/* Coverage area highlight (Wales + SW England) */}
          <ellipse
            cx="155"
            cy="330"
            rx="95"
            ry="75"
            fill="oklch(0.36 0.09 260)"
            opacity="0.08"
          />
          <ellipse
            cx="155"
            cy="330"
            rx="95"
            ry="75"
            fill="none"
            stroke="oklch(0.36 0.09 260)"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            opacity="0.4"
          />
          
          {/* HQ pulse ring */}
          <circle cx="142" cy="312" r="40" fill="url(#pulseGrad)">
            <animate attributeName="r" from="20" to="50" dur="2.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" from="0.6" to="0" dur="2.5s" repeatCount="indefinite" />
          </circle>
          
          {/* City pins */}
          {cities.map((pin) => (
            <g key={pin.label} transform={`translate(${pin.x}, ${pin.y})`}>
              {pin.hq ? (
                <>
                  <circle r="9" fill="oklch(0.69 0.17 47)" />
                  <circle r="4" fill="white" />
                  <text
                    y="-15"
                    textAnchor="middle"
                    className="fill-foreground text-[11px] font-bold"
                    style={{ paintOrder: "stroke", stroke: "white", strokeWidth: 3 }}
                  >
                    {pin.label}
                  </text>
                </>
              ) : (
                <>
                  <circle r="4" fill="oklch(0.36 0.09 260)" />
                  <circle r="1.5" fill="white" />
                  <text
                    x="7"
                    y="3"
                    className="fill-foreground/80 text-[9px] font-medium"
                    style={{ paintOrder: "stroke", stroke: "white", strokeWidth: 2.5 }}
                  >
                    {pin.label}
                  </text>
                </>
              )}
            </g>
          ))}
        </svg>
      </div>
      {!compact && (
        <div className="mt-5 pt-5 border-t">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Areas we cover
          </p>
          <div className="flex flex-wrap gap-2">
            {COVERAGE_AREAS.map((c) => (
              <span
                key={c}
                className="text-xs px-3 py-1.5 rounded-full bg-primary-soft text-primary font-medium"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AccreditationStrip({ layout = "marquee" }: { layout?: "marquee" | "static" }) {
  const loop = [...ACCREDITATION_LOGOS, ...ACCREDITATION_LOGOS];

  const logoImg = (logo: (typeof ACCREDITATION_LOGOS)[number], i: number) => (
    <Link
      key={`${logo.alt}-${i}`}
      to="/accreditations"
      className="shrink-0 flex items-end justify-center h-14 md:h-16 px-3"
      aria-label={logo.alt}
    >
      <img
        src={logo.src}
        alt={logo.alt}
        className="max-h-10 md:max-h-12 w-auto max-w-[9rem] object-contain object-bottom opacity-80 hover:opacity-100 transition-opacity"
        loading="lazy"
      />
    </Link>
  );

  if (layout === "static") {
    return (
      <div className="flex flex-wrap items-end justify-center gap-x-8 gap-y-4 md:gap-x-10 px-2 pb-1">
        {ACCREDITATION_LOGOS.map((logo, i) => logoImg(logo, i))}
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden group"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%)",
      }}
    >
      <div className="flex w-max items-end gap-10 md:gap-14 animate-marquee group-hover:[animation-play-state:paused] pr-10">
        {loop.map((logo, i) => logoImg(logo, i))}
      </div>
    </div>
  );
}

export function AwardsRow() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {AWARDS.map((award, i) => (
        <div key={i} className="rounded-2xl border bg-card p-6 flex flex-col items-center text-center hover:shadow-lift transition-all">
          <img
            src={award.image}
            alt={`${award.title}: ${award.result}`}
            className="h-32 md:h-36 w-auto object-contain mb-4"
            loading="lazy"
          />
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            {award.result}
          </span>
          <h3 className="mt-2 text-base md:text-lg font-semibold leading-snug">
            {award.title}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{award.category}</p>
        </div>
      ))}
    </div>
  );
}


export function ProcessSteps({ steps }: { steps: { title: string; body: string }[] }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {steps.map((step, i) => (
        <div key={step.title} className="rounded-2xl border bg-card p-6 relative">
          <div className="text-5xl font-bold text-accent/20 absolute top-3 right-4">
            {String(i + 1).padStart(2, "0")}
          </div>
          <h3 className="text-lg font-semibold relative">{step.title}</h3>
          <p className="mt-2 text-sm text-muted-foreground relative">{step.body}</p>
        </div>
      ))}
    </div>
  );
}

export function FAQAccordion({ faqs }: { faqs: readonly { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-3 max-w-3xl">
      {faqs.map((faq, i) => (
        <div key={i} className="rounded-xl border bg-card overflow-hidden">
          <button
            type="button"
            className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-secondary/50 transition-colors"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span className="font-semibold">{faq.q}</span>
            <span className="text-2xl text-accent leading-none">{open === i ? "−" : "+"}</span>
          </button>
          {open === i && (
            <div className="px-5 pb-5 text-muted-foreground border-t">
              <p className="pt-3">{faq.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function TestimonialCarousel() {
  return (
    <Suspense fallback={null}>
      <GoogleReviewsCarousel />
      <div className="mt-12 md:mt-16">
        <TrustpilotReviewsCarousel />
      </div>
    </Suspense>
  );
}


export function CrossSellBand({ slugs }: { slugs: string[] }) {
  const items = slugs
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter((s): s is Service => Boolean(s));
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-bold mb-6">You May Also Need</h2>
      <div className="grid md:grid-cols-3 gap-5">
        {items.map((s) => (
          <ServiceCard key={s.slug} service={s} />
        ))}
      </div>
    </div>
  );
}

export function DiagnosticReferralBand({ brand, copy }: { brand: CheckBrand; copy: string }) {
  const b = CHECK_BRANDS[brand];
  return (
    <div className="rounded-2xl bg-primary-soft border border-primary/10 p-7 md:p-9">
      <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
        <img
          src={b.logo}
          alt={`${b.name} logo`}
          className="h-16 w-auto object-contain shrink-0 hidden md:block"
          loading="lazy"
        />
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-accent mb-2">
            Need a survey first?
          </p>
          <h3 className="text-xl md:text-2xl font-bold text-primary">{copy}</h3>
        </div>
        <a
          href={b.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3.5 font-semibold hover:bg-primary/90 transition-colors shrink-0"
        >
          Visit {b.name}
          <ExternalLink className="h-4 w-4" aria-hidden />
        </a>
      </div>
    </div>
  );
}

export function CTABand({
  title = "Ready to get started?",
  description,
  variant = "accent",
}: {
  title?: string;
  description?: string;
  variant?: "accent" | "primary";
}) {
  return (
    <section
      className={cn(
        "py-16 md:py-20",
        variant === "accent" ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"
      )}
    >
      <div className="container-page text-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-balance">{title}</h2>
        {description && (
          <p className="mt-4 text-lg opacity-90 max-w-2xl mx-auto text-pretty">{description}</p>
        )}
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <a
            href={SITE.phoneTel}
            className="inline-flex items-center gap-2 rounded-full bg-background text-foreground px-7 py-4 font-semibold hover:bg-background/90 transition-colors"
          >
            <Phone className="h-5 w-5" aria-hidden />
            Call
          </a>
          <Link
            to="/contact"
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold border-2 transition-colors",
              variant === "accent"
                ? "border-accent-foreground/40 hover:bg-accent-foreground/10"
                : "border-primary-foreground/40 hover:bg-primary-foreground/10"
            )}
          >
            Book a Survey
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}
